import { useState, useRef, useEffect, RefObject, ChangeEvent, FormEvent } from 'react';

import { useDataContext } from 'src/context/data';
import omit from 'lodash/omit';

/* - - - - - - - - - - - - - - - - - - - - - */

export function useCapturePersonalDataIsAllowed() {
  const {
    appState: { currentMessageId, widgetData },
  } = useDataContext();

  const messageData = widgetData?.config?.script[currentMessageId];
  if (!messageData) return false;

  const isFormPageInMessage = messageData.pages.some(page => page.pageType === 'form');

  return Boolean(isFormPageInMessage);
}

/* - - - - - - - - - - - - - - - - - - - - - */

export function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

/* - - - - - - - - - - - - - - - - - - - - - */

export function usePlatform() {
  enum Platform {
    windows = 'Windows',
    mac = 'Macintosh',
  }

  const platform = window.navigator.userAgent.replace(/[()-;]/g, '').split(' ')[1];

  return {
    isWindows: platform === Platform.windows,
    isMac: platform === Platform.mac,
  };
}

/* - - - - - - - - - - - - - - - - - - - - - */

export function useEventListenerOnce<T extends HTMLElement = HTMLDivElement>(
  eventName: keyof WindowEventMap | string,
  handler: (event: Event) => void,
  element?: RefObject<T>,
  useOnce?: boolean,
) {
  const savedHandler = useRef<(event: Event) => void>();

  useEffect(() => {
    const targetElement: T | Window = element?.current || window;
    if (!(targetElement && targetElement.addEventListener)) {
      return;
    }

    if (savedHandler.current !== handler) {
      savedHandler.current = handler;
    }

    const eventListener = (event: Event) => {
      if (!!savedHandler?.current) {
        savedHandler.current(event);
      }
    };

    targetElement.addEventListener(eventName, eventListener, { once: useOnce });

    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, element, handler]);
}

/* - - - - - - - - - - - - - - - - - - - - - */

interface Validation {
  required?: {
    value: boolean;
    message: string;
  };
  pattern?: {
    value: string;
    message: string;
  };
  custom?: {
    isValid: (value: string) => boolean;
    message: string;
  };
}

type ErrorRecord<T> = Partial<Record<keyof T, string>>;

type Validations<T extends {}> = Partial<Record<keyof T, Validation>>;

export const useForm = <T extends Record<keyof T, any> = {}>(options?: {
  validations?: Validations<T>;
  initialValues?: Partial<T>;
  onSubmit?: () => void;
}) => {
  const [data, setData] = useState<T>((options?.initialValues || {}) as T);
  const [errors, setErrors] = useState<ErrorRecord<T>>({});
  const [touchedData, setTouched] = useState<T>({} as T);
  const [submittedOnce, setSubmittedOnce] = useState<boolean>(false);

  // Needs to extend unknown so we can add a generic to an arrow function
  const handleChange = <S extends unknown>(key: keyof T, sanitizeFn?: (value: string) => S) => (
    e: ChangeEvent<HTMLInputElement & HTMLSelectElement>,
  ) => {
    const value = sanitizeFn ? sanitizeFn(e.target.value) : e.target.value;
    setData({
      ...data,
      [key]: value,
    });

    const currentlyTouchedData = { ...touchedData, [key]: true };
    setTouched(currentlyTouchedData);

    if (submittedOnce) {
      if (currentlyTouchedData[key]) {
        const validations = options?.validations;
        if (validations) {
          let valid = true;
          const newErrors: ErrorRecord<T> = {};

          // TODO: Figure out how to move the repetitive validation logic into a separate block.
          const validation = validations[key];
          if (validation?.required?.value && !value) {
            valid = false;
            newErrors[key] = validation?.required?.message;
          }

          const pattern = validation?.pattern;
          if (pattern?.value && !RegExp(pattern.value).test(value as string)) {
            valid = false;
            newErrors[key] = pattern.message;
          }

          const custom = validation?.custom;
          if (custom?.isValid && !custom.isValid(value as string)) {
            valid = false;
            newErrors[key] = custom.message;
          }

          if (!valid) {
            setErrors({ ...errors, ...newErrors });
            return;
          } else {
            setErrors(omit(errors, [key]));
            return;
          }
        }
      }
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validations = options?.validations;
    if (validations) {
      let valid = true;
      const newErrors: ErrorRecord<T> = {};
      for (const key in validations) {
        const value = data[key];
        const validation = validations[key];
        if (validation?.required?.value && !value) {
          valid = false;
          newErrors[key] = validation?.required?.message;
        }

        const pattern = validation?.pattern;
        if (pattern?.value && !RegExp(pattern.value).test(value)) {
          valid = false;
          newErrors[key] = pattern.message;
        }

        const custom = validation?.custom;
        if (custom?.isValid && !custom.isValid(value)) {
          valid = false;
          newErrors[key] = custom.message;
        }
      }

      if (!valid) {
        setErrors(newErrors);
        setSubmittedOnce(true);
        return;
      }
    }

    setErrors({});

    if (options?.onSubmit) {
      options.onSubmit();
    }
  };

  return {
    data,
    handleChange,
    handleSubmit,
    errors,
  };
};

/* - - - - - - - - - - - - - - - - - - - - - */

export const useWindowInnerHeightAndWidthIfResize = () => {
  const [dimensions, setDimensions] = useState<{ windowInnerWidth: number; windowInnerHeight: number }>({
    windowInnerWidth: window.innerWidth,
    windowInnerHeight: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() =>
        setDimensions({ windowInnerWidth: window.innerWidth, windowInnerHeight: window.innerHeight }),
      );
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return dimensions;
};
